import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { InvoiceKind, InvoiceStatus } from "@api/shared-runtime/enums";
import {
  describePaidWith,
  describeRefund,
} from "@api/shared-runtime/billing-collection";
import { formatInvoiceReference } from "@api/shared-runtime/invoice-reference";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTRPCClient } from "@/trpc";
import { saveInvoiceDocument } from "./invoice-document-download";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { InvoiceLinesTable } from "@/components/billing-plan/invoice-lines-table";
import {
  formatInvoicePeriod,
  formatPlanCurrency,
  formatPlanInstant,
} from "@/components/billing-plan/billing-plan-values";
import {
  CUSTOMER_INVOICE_STATUS_LABELS,
  PAY_INVOICE_DESCRIPTION,
  PAYMENT_FAILED_NOTICE,
  PAYMENT_PENDING_LABEL,
  PAYMENT_PENDING_NOTICE,
  type CustomerInvoice,
} from "./invoice-presentation";
import { PayInvoiceLink } from "./pay-invoice-link";
import { PayInvoiceButton, type PayableAccount } from "./pay-invoice-button";

export function InvoiceDetailSheet({
  invoice,
  organizationBusinessUnitId,
  canManage,
  account,
  onOpenChange,
}: {
  invoice: CustomerInvoice | null;
  organizationBusinessUnitId: string;
  canManage: boolean;
  account: PayableAccount | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={Boolean(invoice)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            Invoice
            {invoice?.paymentPending && (
              <Badge variant="secondary">{PAYMENT_PENDING_LABEL}</Badge>
            )}
            {invoice && !invoice.paymentPending && (
              <Badge
                variant={
                  invoice.status === InvoiceStatus.Paid ? "default" : "outline"
                }
              >
                {CUSTOMER_INVOICE_STATUS_LABELS[invoice.status]}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Charges and payment details for this invoice
          </SheetDescription>
        </SheetHeader>
        {invoice && (
          <InvoiceDetailBody
            invoice={invoice}
            organizationBusinessUnitId={organizationBusinessUnitId}
            canManage={canManage}
            account={account}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function InvoiceDetailBody({
  invoice,
  organizationBusinessUnitId,
  canManage,
  account,
}: {
  invoice: CustomerInvoice;
  organizationBusinessUnitId: string;
  canManage: boolean;
  account: PayableAccount | null;
}) {
  const usagePeriod =
    invoice.usagePeriodStart && invoice.usagePeriodEnd
      ? { start: invoice.usagePeriodStart, end: invoice.usagePeriodEnd }
      : null;
  return (
    <div className="mt-6 space-y-6 text-sm">
      <dl className="space-y-3">
        {invoice.paymentServiceInvoiceNumber ? (
          <Row label="Invoice number">
            {invoice.paymentServiceInvoiceNumber}
          </Row>
        ) : (
          <Row label="Reference">{formatInvoiceReference(invoice.id)}</Row>
        )}
        {invoice.issuedAt && (
          <Row label="Issued">{formatPlanInstant(invoice.issuedAt)}</Row>
        )}
        {invoice.kind === InvoiceKind.Cycle && (
          <Row label="Base fee period">
            {formatInvoicePeriod({
              start: invoice.cycleStart,
              end: invoice.cycleEnd,
            })}
          </Row>
        )}
        {usagePeriod && (
          <Row label="Usage period">{formatInvoicePeriod(usagePeriod)}</Row>
        )}
        {invoice.taxAmount !== null && (
          <>
            <Row label="Subtotal">
              {formatPlanCurrency(invoice.subtotalAmount)}
            </Row>
            <Row label="Sales tax">{formatPlanCurrency(invoice.taxAmount)}</Row>
          </>
        )}
        <Row label="Total">
          {formatPlanCurrency(invoice.totalAmount ?? invoice.subtotalAmount)}
        </Row>
        {invoice.paymentPending && invoice.issuedAt && (
          <Row label="Charged">{formatPlanInstant(invoice.issuedAt)}</Row>
        )}
        {invoice.status === InvoiceStatus.Open &&
          !invoice.paymentPending &&
          invoice.dueAt && (
            <Row label="Due">{formatPlanInstant(invoice.dueAt)}</Row>
          )}
        {invoice.status === InvoiceStatus.Paid && invoice.paidAt && (
          <Row label="Paid">{formatPlanInstant(invoice.paidAt)}</Row>
        )}
        {describePaidWith(invoice) && (
          <Row label="Paid with">{describePaidWith(invoice)}</Row>
        )}
        {describeRefund(invoice) && (
          <Row label="Refunded">
            {formatPlanCurrency(invoice.amountRefunded ?? "0")}
            {invoice.refundedAt
              ? ` on ${formatPlanInstant(invoice.refundedAt)}`
              : ""}
          </Row>
        )}
      </dl>
      {invoice.hostedInvoiceUrl && (
        <div className="space-y-2">
          <PayInvoiceLink invoice={invoice} />
          <p className="text-xs text-muted-foreground">
            {PAY_INVOICE_DESCRIPTION}
          </p>
        </div>
      )}
      {invoice.paymentPending && (
        <p className="rounded-md border bg-muted p-3 text-sm">
          {PAYMENT_PENDING_NOTICE}
        </p>
      )}
      {invoice.paymentFailed && invoice.status === InvoiceStatus.Open && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          {PAYMENT_FAILED_NOTICE}
        </p>
      )}
      {canManage && (
        <PayInvoiceButton
          invoice={invoice}
          organizationBusinessUnitId={organizationBusinessUnitId}
          account={account}
        />
      )}

      <InvoiceLinesTable
        lines={invoice.lines}
        totalAmount={invoice.subtotalAmount}
        descriptionHeader="Charge"
        formatAmount={formatPlanCurrency}
      />

      <DocumentActions
        invoice={invoice}
        organizationBusinessUnitId={organizationBusinessUnitId}
      />
    </div>
  );
}

/**
 * Stripe's PDF is the official document for an on-terms invoice (it carries
 * credit notes and tax); a direct charge has none, so Saile renders one.
 */
function DocumentActions({
  invoice,
  organizationBusinessUnitId,
}: {
  invoice: CustomerInvoice;
  organizationBusinessUnitId: string;
}) {
  const trpcClient = useTRPCClient();
  const download = useMutation({
    mutationFn: async () =>
      saveInvoiceDocument(
        await trpcClient.organization.getInvoiceDocument.query({
          organizationBusinessUnitId,
          invoiceId: invoice.id,
        }),
      ),
    onError: () => toast.error("Couldn't download the invoice"),
  });
  if (invoice.pdfUrl) {
    return (
      <a
        href={invoice.pdfUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-block text-primary underline"
      >
        Download PDF
      </a>
    );
  }
  return (
    <Button
      type="button"
      variant="link"
      className="h-auto p-0"
      disabled={download.isPending}
      onClick={() => download.mutate()}
    >
      {download.isPending ? "Preparing PDF..." : "Download PDF"}
    </Button>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
